import { logger } from 'common/services/logger';

import { GenerationErrorCode, GenerationState, NatalieEventV2Status, V2ErrorCode } from './natalieCreateV2.types';
import { NatalieEventStatus } from './natalieEvent.types';

import type {
  DuplicateWarningDto,
  GenerationDto,
  NatalieEventV2CreatedDto,
  NatalieEventV2DraftDto,
  V2ErrorResponse,
} from './natalieCreateV2.types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * The boundary between the v2 creation contract and this flow.
 *
 * The generated types declare the enum-valued fields as closed unions, which is a
 * promise about the spec rather than about the bytes on the wire: the service can add
 * a state or a code before this bundle is rebuilt. So every one of them is checked
 * here, and an unrecognised value is logged and replaced rather than thrown on — a
 * future service-side addition must not crash the organiser's screen.
 */

const isMemberOf = <T extends string>(members: Record<string, T>, value: unknown): value is T =>
  typeof value === 'string' && (Object.values(members) as string[]).includes(value);

/** The draft's generation, with its two enum-valued fields checked. */
export type NatalieDraftGeneration = Omit<GenerationDto, 'state' | 'errorCode'> & {
  state: GenerationState;
  errorCode?: GenerationErrorCode;
};

/**
 * The draft as this flow consumes it. The contract's blocks are kept intact —
 * whether a field takes effect immediately or only on publication is decided by
 * where it sits in the contract rather than by its name, so flattening them would
 * move that decision into a list of field names.
 *
 * `prompt` and `context` are optional here because they are optional on the read:
 * the contract serves them only to a caller who may manage the event. This flow is
 * admin-gated so they are always there in practice, but the mapper sits at the wire
 * and is not the place to assert a precondition nothing checks.
 */
export type NatalieDraft = Omit<NatalieEventV2DraftDto, 'status' | 'generation'> & {
  /**
   * The v2 lifecycle, which has three states rather than two.
   *
   * Narrowed against the v1 enum before, which has no `DRAFT`: every read of an unpublished draft
   * failed the membership check, warned, and came back as `ACTIVE` — so a draft nobody had
   * published reported itself as live, and Review's polling produced a warning per poll.
   */
  status: NatalieEventV2Status;
  generation?: NatalieDraftGeneration;
};

/** What create answers with: the draft, plus the advisory duplicate warning when one applies. */
/**
 * A draft known to carry the manager-only members.
 *
 * Not what the mapper promises — see {@link NatalieDraft} — but what a caller that CONSTRUCTS a
 * draft is stating: a fixture or a simulated service standing in for the organiser's own read has
 * both `prompt` and `context`, and is not modelling the member's projection that omits them.
 */
export type ManagedNatalieDraft = NatalieDraft & Required<Pick<NatalieDraft, 'context' | 'prompt'>>;

export type CreatedNatalieDraft = {
  event: NatalieDraft;
  duplicateWarning?: DuplicateWarningDto;
};

/**
 * An unrecognised state is reported as failed, which leaves the organiser a retry.
 * Reporting it as running instead would poll forever if the new state is terminal,
 * and the contract's guarantee that RUNNING always ends does not extend to a value
 * this client has never heard of.
 */
const narrowGenerationState = (state: unknown): GenerationState => {
  if (isMemberOf(GenerationState, state)) return state;
  logger.warn(`[natalie v2 mappers] Unknown generation state "${String(state)}", reporting it as failed`, { state });
  return GenerationState.Failed;
};

/**
 * An unrecognised code is dropped rather than replaced: the copy for each declared
 * code is this flow's own, and it already carries a fallback for a failure it cannot
 * name, so a missing code and an unknown one read the same to the organiser.
 */
const narrowGenerationErrorCode = (errorCode: unknown): GenerationErrorCode | undefined => {
  if (errorCode === undefined || errorCode === null) return undefined;
  if (isMemberOf(GenerationErrorCode, errorCode)) return errorCode;
  logger.warn(`[natalie v2 mappers] Unknown generation error code "${String(errorCode)}", dropping it`, { errorCode });
  return undefined;
};

/**
 * Defaults to Active, matching the v1 mapper. Fail-open is the lesser of the two
 * risks on this screen: the flow's own actions are gated on generation and on the
 * published version, not on the lifecycle status.
 */
export const narrowEventStatus = (status: unknown): NatalieEventV2Status => {
  if (isMemberOf(NatalieEventV2Status, status)) return status;
  logger.warn(`[natalie v2 mappers] Unknown event status "${String(status)}", defaulting to Active`, { status });
  return NatalieEventV2Status.Active;
};

const mapGenerationFromApi = (generation: GenerationDto): NatalieDraftGeneration => ({
  ...generation,
  state: narrowGenerationState(generation.state),
  errorCode: narrowGenerationErrorCode(generation.errorCode),
});

export const mapNatalieDraftFromApi = (data: NatalieEventV2DraftDto): NatalieDraft => ({
  ...data,
  status: narrowEventStatus(data.status),
  // Absent when no generation has ever run — an event created with field values
  // supplied directly. Kept absent rather than given a state of its own.
  generation: data.generation ? mapGenerationFromApi(data.generation) : undefined,
});

export const mapCreatedNatalieDraftFromApi = (data: NatalieEventV2CreatedDto): CreatedNatalieDraft => ({
  event: mapNatalieDraftFromApi(data.event),
  duplicateWarning: data.duplicateWarning,
});

const readFirstError = (error: unknown) => ((error ?? {}) as { data?: V2ErrorResponse }).data?.errors?.[0];

/**
 * The refusal's stable code.
 *
 * Keyed off `errorId` rather than the HTTP status, which is too coarse — one 409 covers
 * "a generation is running", "nothing to publish" and a reused idempotency key, and each
 * of those needs a different sentence — and rather than the service's `message`, which is
 * written for API consumers and would bypass translation.
 *
 * Absent yields nothing without a word: a transport failure carries no envelope at all,
 * and that is ordinary. An `errorId` this client does not know is worth a line in the log,
 * because it means the service's set has grown past this bundle's copy of the spec.
 */
export const extractV2ErrorCode = (error: unknown): V2ErrorCode | undefined => {
  const errorId = readFirstError(error)?.errorId;
  if (errorId === undefined) return undefined;
  if (isMemberOf(V2ErrorCode, errorId)) return errorId;

  logger.warn(`[natalie v2 mappers] Unknown v2 error code "${String(errorId)}"`, { errorId });
  return undefined;
};

/**
 * The event the first create is still making, off a `REQUEST_IN_FLIGHT` refusal. The
 * service attaches it precisely so a colliding retry can poll that event instead of
 * sending again, which is what keeps one submit from producing two events.
 */
export const extractRequestInFlightEventId = (error: unknown): string | undefined => {
  if (extractV2ErrorCode(error) !== V2ErrorCode.RequestInFlight) return undefined;

  return readFirstError(error)?.metadata?.eventId;
};

/**
 * The request field a refusal is ABOUT, off `metadata.fieldName`.
 *
 * The contract attaches it "so a client can map it to a form field", which is exactly what the
 * two screens do with it: a duplicate name or a rejected value belongs on the input the organiser
 * is looking at, not only in a sentence under the form.
 *
 * Returned as the contract's own path — `eventConfig.name`, `prompt.greeting` — never translated
 * here. Which form control that is depends on the screen, and the screens already know it from
 * the body they built; a lookup table in this file would be a second copy of that knowledge,
 * free to drift from the one that actually addresses the write.
 *
 * Absent for the refusals that are about the request rather than a value in it — a stale
 * revision, a generation running — and absent when the service sends none, which the contract
 * permits: `fieldName` is optional.
 */
export const extractV2ErrorFieldName = (error: unknown): string | undefined =>
  readFirstError(error)?.metadata?.fieldName;

/**
 * The status a failure with no answer at all is reported under: the request never reached the
 * service, or its answer never reached us. The query layer's own name for it. Only the generation
 * start reports it — see `generateNatalieEventFields` — because it is the one write a caller sends
 * again on it without asking the organiser.
 */
export const NO_RESPONSE_STATUS = 'FETCH_ERROR' satisfies FetchBaseQueryError['status'];

export const didNotReachService = (error: unknown): boolean =>
  (error as { status?: unknown } | undefined)?.status === NO_RESPONSE_STATUS;
